// A ponte entre o `inputs` que a tool devolve e o que o cartão desenha
// (spec-007 do `project-onboarding-with-ai-fee-fixo`, [T107] — US-2/US-2b).
//
// O cartão CALCULA, não busca: o `S` vem pronto no `tool_result` e quem produz
// os números é o `simulate()` do laboratório — o MESMO motor da tela, sem
// segunda implementação. Esta ponte existe para que a decisão de "isto pode
// virar número na tela?" aconteça UMA vez, antes de qualquer componente montar.
//
// ⚠️ Ela não valida a ENTRADA. A guarda é de SAÍDA
// (`utils/fee-lab-publicavel.js`) pela razão medida em 24/08/2026: validar a
// entrada pega o campo AUSENTE e não pega o presente-e-inútil; a saída pega os
// dois, porque é onde o defeito se manifesta — com um `S` sem
// `rent`/`alfa`/`aporteAnual`, os custos em BPS saem CERTOS e todo valor em
// REAIS sai `NaN`, que vira `null` ao serializar.
import {
  simulate,
  simulateEvolucaoPatrimonio,
} from '@modules/fee-fixo/utils/fee-lab-engine'
import {
  evolucaoPublicavel,
  resultadoPublicavel,
} from '@modules/fee-fixo/utils/fee-lab-publicavel'

/**
 * Grupo 4 do contrato do `S` — só o gráfico de evolução o usa.
 *
 * Ele é o único grupo cuja ausência NÃO produz `NaN`: sem ele,
 * `simulateEvolucaoPatrimonio` devolve UM ponto (`ages: [0]`, `delta: 0`). Por
 * isso a checagem é própria, e não cabe na guarda de plausibilidade.
 */
export const CAMPOS_DA_LONGEVIDADE = [
  'idadeAtual',
  'idadeAposentadoria',
  'longevidade',
  'retiradaAnual',
]

/** Número que dá para calcular: existe, é finito e não é `NaN`. */
const numero = (valor) => typeof valor === 'number' && Number.isFinite(valor)

/**
 * O grupo 4 veio COMPLETO?
 *
 * A régua é o TIPO, não a coerção — `Number('') === 0` faria uma idade ausente
 * virar recém-nascido, e a curva sairia com 90 pontos de uma vida que ninguém
 * informou. É a mesma régua da I-A3 do cartão de estado.
 */
export const temLongevidade = (S) =>
  Boolean(S) && CAMPOS_DA_LONGEVIDADE.every((campo) => numero(S[campo]))

/**
 * O que o cartão precisa para decidir o que desenhar.
 *
 * ★ Devolve `publicavel: false` também quando o motor LANÇA. A guarda existe
 * para impedir número errado calado; uma exceção não tratada aqui viraria tela
 * branca no meio do thread, que é o mesmo problema com outra roupa.
 *
 * @param {Object} inputs O `S` dos quatro grupos, como a tool o devolveu.
 * @returns {{S: Object, resultado: ?Object, publicavel: boolean,
 *   evolucao: ?Object, mostraEvolucao: boolean}}
 */
export const simulacaoDoChat = (inputs) => {
  const S = inputs && typeof inputs === 'object' ? inputs : {}
  let resultado = null
  try {
    resultado = simulate(S)
  } catch (erro) {
    return { S, resultado: null, publicavel: false, evolucao: null, mostraEvolucao: false }
  }
  const publicavel = resultadoPublicavel(resultado)
  // A evolução só é calculada quando há grupo 4 E o resto fechou: gráfico ao
  // lado de "o cálculo não fechou" seria a tela dizendo as duas coisas.
  const evolucao =
    publicavel && temLongevidade(S) ? simulateEvolucaoPatrimonio(S) : null
  return {
    S,
    resultado,
    publicavel,
    evolucao,
    mostraEvolucao: evolucaoPublicavel(evolucao),
  }
}
